import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TYPE_META = {
  escrow_fund: { t: "Escrow funding", cls: "text-rose-700" },
  award_payout: { t: "Award payout", cls: "text-emerald-700" },
  reviewer_honorarium: { t: "Review honorarium", cls: "text-cyan-700" },
  platform_fee: { t: "Platform fee", cls: "text-[#D4AF37]" },
  refund: { t: "Refund", cls: "text-emerald-700" },
};

export default function Ledger() {
  const { user } = useAuth();
  const [data, setData] = useState({ balance: 0, entries: [] });
  const [adminData, setAdminData] = useState(null);

  useEffect(() => {
    api.get("/ledger/me").then((r) => setData(r.data)).catch(() => {});
    if (user?.role === "admin") api.get("/ledger/all").then((r) => setAdminData(r.data)).catch(() => {});
  }, [user]);

  const fmt = (n) => `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  return (
    <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8" data-testid="ledger-page">
      <p className="eyebrow mb-2">Earnings ledger</p>
      <h1 className="font-display text-3xl tracking-tight text-slate-900 sm:text-4xl">Financial Record</h1>
      <p className="mt-2 text-slate-500">A transactional audit trail of escrows, payouts, honorariums, and fees. <span className="text-[#E61A27]">Simulated — real payments disabled.</span></p>

      <div className="axiom-card mt-8 p-6">
        <p className="eyebrow">Your balance</p>
        <p className={`mt-1 font-display text-4xl ${data.balance >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="ledger-balance">{fmt(data.balance)}</p>
      </div>

      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-sm" data-testid="ledger-table">
          <thead>
            <tr className="border-b border-[#CBD5E1] text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="py-3 pr-4">Type</th><th className="py-3 pr-4">Description</th><th className="py-3 pr-4 text-right">Amount</th><th className="py-3 text-right">Date</th>
            </tr>
          </thead>
          <tbody>
            {data.entries.map((e) => {
              const m = TYPE_META[e.type] || { t: e.type, cls: "text-slate-600" };
              return (
                <tr key={e.id} className="border-b border-[#CBD5E1]/50" data-testid={`ledger-row-${e.id}`}>
                  <td className="py-3 pr-4"><span className={`font-mono text-xs ${m.cls}`}>{m.t}</span></td>
                  <td className="py-3 pr-4 text-slate-500">{e.description}</td>
                  <td className={`py-3 pr-4 text-right font-mono ${e.amount < 0 ? "text-rose-700" : "text-emerald-700"}`}>{fmt(e.amount)}</td>
                  <td className="py-3 text-right text-xs text-slate-500">{new Date(e.created_at).toLocaleDateString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {data.entries.length === 0 && <p className="mt-6 text-slate-500">No ledger entries yet.</p>}
      </div>

      {adminData && (
        <div className="mt-12">
          <p className="eyebrow mb-3">Platform reconciliation (admin)</p>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            {Object.entries(adminData.totals).map(([k, v]) => {
              const m = TYPE_META[k] || { t: k, cls: "text-slate-600" };
              return (
                <div key={k} className="axiom-card p-4" data-testid={`total-${k}`}>
                  <p className="text-xs text-slate-500">{m.t}</p>
                  <p className={`mt-1 font-mono ${m.cls}`}>{fmt(v)}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
